<template>
  <nav>
    <Tabs :tabs :active="activeId" @click="onClick" />

    <DevOnly v-if="debugMode">
      <ScrollSpyDebug :areas :trigger-y />
    </DevOnly>
  </nav>
</template>

<script lang="ts" setup>
import { useDebounceFn, useRafFn } from '@vueuse/core'
import type { TabItem } from '~/components/Tabs/types'

const props = defineProps<{
  tabs: TabItem[]
  disabled?: boolean
}>()

const { height: windowHeight } = useViewport()

// The positions on the Y axis, keyed by tab ID.
const positions = reactive<Record<string, number>>({})
const debugMode = false
const activeIdOverride = ref('')
const prevScrollY = ref(-1)

// The position on the Y axis relative to the viewport.
// The area that intersects with this point will be deemed active.
const triggerY = computed(() => Math.round(windowHeight.value / 3))

// The areas for each tab.
const areas = computed<
  {
    yStart: number
    yEnd: number
    id: string
  }[]
>(() =>
  Object.entries(positions)
    .map(([id, y]) => ({ id, y }))
    .sort((a, b) => a.y - b.y) // Sort from lowest to highest.
    .map(({ id, y }, index, arr) => {
      const isFirst = index === 0
      const isLast = index === arr.length - 1

      // The y position where the area starts.
      // The first area's start position is always 0, since we want the first
      // tab to be active even if the first area is below the fold.
      const yStart = isFirst ? 0 : y

      // The y position where the area ends.
      // For the last area, the area ends at the "end of the document".
      // For all others, the area ends at the y position of the next area in
      // the array.
      const yEnd = isLast
        ? document.documentElement.offsetHeight
        : arr[index + 1].y

      return { id, yStart, yEnd }
    }),
)

const activeId = computed(() => {
  // Return the active override if available.
  if (activeIdOverride.value) {
    return activeIdOverride.value
  }

  // The trigger position.
  const position = prevScrollY.value + triggerY.value

  // Find the area that intersects with the trigger point.
  const activeArea = areas.value.find(
    (area) => position >= area.yStart && position < area.yEnd,
  )

  return activeArea?.id
})

const removeOverride = useDebounceFn(() => {
  activeIdOverride.value = ''
}, 1000)

function onClick(id: string) {
  const element = document.getElementById(id)
  if (!(element instanceof HTMLElement)) {
    return
  }

  element.scrollIntoView({
    behavior: 'smooth',
  })

  // Set the override.
  activeIdOverride.value = id

  // This will remove the override again after the delay.
  removeOverride()
}

function setPositions() {
  if (props.disabled) {
    return
  }

  const newScrollY = window.scrollY

  // Skip recalculating when scroll position did not change since last check.
  if (prevScrollY.value === newScrollY) {
    return
  }

  for (let i = 0; i < props.tabs.length; i++) {
    const tab = props.tabs[i]
    const id = tab.id

    const element = document.getElementById(id)
    if (!(element instanceof HTMLElement)) {
      continue
    }

    const rect = element.getBoundingClientRect()
    // Calculate the new Y position of the area.
    // We round it to the nearest 5px, to ignore small fluctuations in heights.
    const rectY = Math.round((rect.y + newScrollY) / 5) * 5

    // Only update when it actually changed.
    if (positions[id] !== rectY) {
      positions[id] = rectY
    }
  }

  prevScrollY.value = newScrollY
}

useRafFn(setPositions, {
  // Runs the callback once per second.
  fpsLimit: 2,
})

onMounted(setPositions)
</script>
